import React, { useEffect, useRef, useState } from "react";
import {
  prepareDisplayNetwork,
  renderDisplayNetwork,
  selectDisplayUnit,
  detectDeviceClass,
  watchDisplayCreative,
} from "./providers/AdProviderResolver";
import { trackDisplayAdEvent, getAdSessionId } from "./advertisingApi";
import "./DisplayAd.css";

function unitFingerprint(network) {
  if (!network) return "";
  return String(network.provider_unit_id || network.unit_id || "");
}

function buildEventId(prefix, network, placementKey, eventType) {
  return (
    prefix +
    ":" +
    (network.provider || "") +
    ":" +
    (network.unit_id || network.provider_unit_id || "") +
    ":" +
    placementKey +
    ":" +
    eventType +
    ":" +
    Date.now()
  );
}

/**
 * Renders a network display ad from API payload.
 * Adapter selects the configured unit at init; remounts only when the
 * selected unit actually changes (orientation / breakpoint), not every resize.
 *
 * Event lifecycle per activation:
 *  requested — activation starts;
 *  loaded — provider loader ready and the unit mounted (not proof of an ad);
 *  rendered + impression — once, when the first creative appears in the slot;
 *  no_fill — loader mount refused, unit never activated, or no creative within
 *    the no-fill window after the bid (onNoFill fires; slot collapses).
 * Provider-side refreshes inside an already-filled slot are not re-counted,
 * nor are refresh_seconds re-renders; unmounting before a creative appears
 * records no rendered / impression.
 *
 * The slot is data-creative-state="pending" (transparent) from activation
 * until that first creative is confirmed, then "revealed"; provider markup
 * that never yields a creative is never painted before the no-fill collapse.
 */
export default function DisplayAd({
  network,
  placementKey,
  className = "",
  onNoFill = null,
  active = true,
}) {
  var hostRef = useRef(null);
  var refreshTimer = useRef(null);
  var onNoFillRef = useRef(onNoFill);
  onNoFillRef.current = onNoFill;
  var [failed, setFailed] = useState(false);
  var [deviceClass, setDeviceClass] = useState(function () {
    return detectDeviceClass();
  });

  var resolved = selectDisplayUnit(network, { device: deviceClass });
  var unitKey = unitFingerprint(resolved);

  // Warm the provider loader as soon as an ad-capable slot mounts, even while
  // inactive (e.g. a banner slide not yet shown). Script only — no unit, no bid.
  useEffect(
    function () {
      if (resolved && resolved.display) prepareDisplayNetwork(resolved);
    },
    [unitKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    function () {
      function syncDevice() {
        var next = detectDeviceClass();
        setDeviceClass(function (prev) {
          return prev === next ? prev : next;
        });
      }
      if (typeof window === "undefined") return undefined;
      window.addEventListener("orientationchange", syncDevice);
      window.addEventListener("resize", syncDevice);
      return function () {
        window.removeEventListener("orientationchange", syncDevice);
        window.removeEventListener("resize", syncDevice);
      };
    },
    []
  );

  useEffect(
    function () {
      if (!active) return undefined;
      if (!resolved || !resolved.display) return undefined;
      var cancelled = false;
      var container = hostRef.current;
      var selected = resolved;
      var stopWatch = null;
      var counted = false;

      function reportNoFill() {
        setFailed(true);
        trackDisplayAdEvent({
          provider: selected.provider,
          unit_id: selected.unit_id,
          provider_unit_id: selected.provider_unit_id,
          placement_key: placementKey,
          event_type: "no_fill",
          device: deviceClass,
          page: typeof window !== "undefined" ? window.location.pathname : "",
          client_event_id: buildEventId(
            "nf",
            selected,
            placementKey,
            "no_fill"
          ),
        });
        if (typeof onNoFillRef.current === "function") onNoFillRef.current();
      }

      function reportCreative() {
        if (counted) return;
        counted = true;
        if (container) container.setAttribute("data-creative-state", "revealed");
        trackDisplayAdEvent({
          provider: selected.provider,
          unit_id: selected.unit_id,
          provider_unit_id: selected.provider_unit_id,
          placement_key: placementKey,
          event_type: "rendered",
          device: deviceClass,
          page: typeof window !== "undefined" ? window.location.pathname : "",
          client_event_id: buildEventId(
            "rd",
            selected,
            placementKey,
            "rendered"
          ),
        });
        trackDisplayAdEvent({
          provider: selected.provider,
          unit_id: selected.unit_id,
          provider_unit_id: selected.provider_unit_id,
          placement_key: placementKey,
          event_type: "impression",
          device: deviceClass,
          session_id: getAdSessionId(),
          page: typeof window !== "undefined" ? window.location.pathname : "",
          client_event_id: buildEventId(
            "im",
            selected,
            placementKey,
            "impression"
          ),
        });
      }

      if (container) container.setAttribute("data-creative-state", "pending");

      trackDisplayAdEvent({
        provider: selected.provider,
        unit_id: selected.unit_id,
        provider_unit_id: selected.provider_unit_id,
        placement_key: placementKey,
        event_type: "requested",
        device: deviceClass,
        page: typeof window !== "undefined" ? window.location.pathname : "",
        client_event_id: buildEventId("req", selected, placementKey, "requested"),
      });

      renderDisplayNetwork(container, selected, { placement_key: placementKey })
        .then(function (el) {
          if (cancelled) return;
          if (!el) {
            reportNoFill();
            return;
          }
          trackDisplayAdEvent({
            provider: selected.provider,
            unit_id: selected.unit_id,
            provider_unit_id: selected.provider_unit_id,
            placement_key: placementKey,
            event_type: "loaded",
            device: deviceClass,
            page: typeof window !== "undefined" ? window.location.pathname : "",
            client_event_id: buildEventId(
              "ld",
              selected,
              placementKey,
              "loaded"
            ),
          });
          stopWatch = watchDisplayCreative(container, {
            onCreative: function () {
              if (!cancelled) reportCreative();
            },
            onNoFill: function () {
              if (cancelled) return;
              if (typeof container._tpgCaCancel === "function") {
                container._tpgCaCancel();
                container._tpgCaCancel = null;
              }
              if (refreshTimer.current) {
                window.clearInterval(refreshTimer.current);
                refreshTimer.current = null;
              }
              reportNoFill();
            },
          });

          var refreshSec = parseInt(selected.refresh_seconds, 10);
          if (refreshSec > 0 && container) {
            refreshTimer.current = window.setInterval(function () {
              if (!container || cancelled) return;
              renderDisplayNetwork(container, selected, { placement_key: placementKey });
            }, refreshSec * 1000);
          }
        })
        .catch(function () {
          if (cancelled) return;
          setFailed(true);
          trackDisplayAdEvent({
            provider: selected.provider,
            unit_id: selected.unit_id,
            provider_unit_id: selected.provider_unit_id,
            placement_key: placementKey,
            event_type: "error",
            device: deviceClass,
            error_code: "render_failed",
            page: typeof window !== "undefined" ? window.location.pathname : "",
            client_event_id: buildEventId(
              "er",
              selected,
              placementKey,
              "error"
            ),
          });
          if (typeof onNoFillRef.current === "function") onNoFillRef.current();
        });

      return function () {
        cancelled = true;
        if (stopWatch) {
          stopWatch();
          stopWatch = null;
        }
        if (
          container &&
          typeof container._tpgCaCancel === "function"
        ) {
          container._tpgCaCancel();
          container._tpgCaCancel = null;
        }
        if (refreshTimer.current) {
          window.clearInterval(refreshTimer.current);
          refreshTimer.current = null;
        }
      };
    },
    [unitKey, placementKey, deviceClass, active]
  );

  if (!resolved || !resolved.display) return null;
  if (failed) return null;
  if (!active) return null;

  var w = resolved.width ? Number(resolved.width) : null;
  var h = resolved.height ? Number(resolved.height) : null;
  // Fill available placement width. Inventory w×h are ratio metadata
  // (data-ad-*) for the adapter sizer — not CSS max display size.

  return (
    <div
      className={"display-ad " + className}
      data-provider={resolved.provider || ""}
      data-placement={placementKey}
      data-unit={unitKey}
      data-ad-width={w && w > 0 ? String(w) : undefined}
      data-ad-height={h && h > 0 ? String(h) : undefined}
      style={{ width: "100%", maxWidth: "100%" }}
    >
      <span className="display-ad__label">Advertisement</span>
      <div ref={hostRef} className="display-ad__slot" aria-live="polite" />
    </div>
  );
}
